/** Group labor rates for quote plans. Labor hours × rate = cost. */

export const QUOTE_RATE_PROFILES = {
    design: { id: "design", label: "Engineering / scope & design", hourlyRate: 115 },
    rd: { id: "rd", label: "R&D / feasibility", hourlyRate: 115 },
    installation: { id: "installation", label: "Commissioning / installation", hourlyRate: 135 },
    software: { id: "software", label: "Custom software", hourlyRate: 195 },
    training: { id: "training", label: "Training / documentation", hourlyRate: 105 },
    pm: { id: "pm", label: "Project management", hourlyRate: 105 },
    travel: { id: "travel", label: "Travel time", hourlyRate: 95 },
};

const GROUP_KEY_TO_PROFILE = {
    electrical_enclosure: "design",
    "electrical-enclosure": "design",
    robot_cell: "design",
    "robot-cell": "design",
    eoat: "design",
    existing_machine_integration: "design",
    existing_machine: "design",
    "existing-machine": "design",
    machine_cell: "design",
    machine_tending: "design",
    "machine-tending": "design",
    basic: "design",
    advanced: "design",
    palletizing: "design",
    packaging: "design",
    inspection: "design",
    guarding_safety: "design",
    safety: "design",
    handshake: "design",
    infeed: "design",
    integration_split: "design",
    shop_package: "design",
    rd_time: "rd",
    "rd-time": "rd",
    installation: "installation",
    software: "software",
    training_handover: "training",
    training_docs: "training",
    project_management: "pm",
    pm: "pm",
    travel_time: "travel",
    travel: "travel",
    training: "training",
};

const LABEL_HINTS = [
    [/r\s*&\s*d|research|feasib/i, "rd"],
    [/project\s*manag|coordinat/i, "pm"],
    [/travel/i, "travel"],
    [/install|commission/i, "installation"],
    [/software|program|erp/i, "software"],
    [/train|handover/i, "training"],
    [/enclos|electrical/i, "design"],
];

const DYNAMIC_RATE_KEYS = {
    design: ["engineering"],
    rd: ["engineering"],
    installation: ["onsite_billed", "onsite_day"],
    software: ["app_development"],
    training: ["training_docs"],
    pm: ["project_management"],
    travel: ["travel_time"],
};

function profileForId(profileId, rates) {
    const catalogRate = (rates || []).find((row) => DYNAMIC_RATE_KEYS[profileId]?.includes(row.rate_key));
    if (catalogRate) {
        return {
            id: profileId,
            label: catalogRate.label,
            hourlyRate: Number(catalogRate.suggested_rate) || QUOTE_RATE_PROFILES[profileId].hourlyRate,
        };
    }
    return QUOTE_RATE_PROFILES[profileId];
}

export function rateProfileForGroup(group, rates = []) {
    const key = String(group?.group_key || "").trim();
    const profileId = GROUP_KEY_TO_PROFILE[key];
    if (profileId) return profileForId(profileId, rates);
    const label = String(group?.label || "");
    for (const [re, id] of LABEL_HINTS) {
        if (re.test(label)) return profileForId(id, rates);
    }
    return profileForId("design", rates);
}

export function parseLaborHours(value) {
    if (value === "" || value == null) return 0;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

export function laborCostFromHours(hours, profile) {
    const h = parseLaborHours(hours);
    const rate = Number(profile?.hourlyRate) || 0;
    return h > 0 && rate > 0 ? h * rate : 0;
}

export function groupQuoteOwn(group, rates = []) {
    const profile = rateProfileForGroup(group, rates);
    const laborHours = parseLaborHours(group?.labor_hours);
    const cost = laborCostFromHours(laborHours, profile);
    const purchases = Math.max(0, Number(group?.material_cost) || 0);
    return {
        profile,
        laborHours,
        cost,
        purchases,
        total: cost + purchases,
    };
}

export function rollupGroupQuote(node, rates = []) {
    const own = groupQuoteOwn(node, rates);
    let childHours = 0;
    let childCost = 0;
    let childPurchases = 0;
    for (const child of node?.children || []) {
        const q = rollupGroupQuote(child, rates);
        childHours += q.laborHours;
        childCost += q.cost;
        childPurchases += q.purchases;
    }
    return {
        profile: own.profile,
        laborHours: own.laborHours + childHours,
        cost: own.cost + childCost,
        purchases: own.purchases + childPurchases,
        total: own.cost + own.purchases + childCost + childPurchases,
        own,
        children: {
            laborHours: childHours,
            cost: childCost,
            purchases: childPurchases,
            total: childCost + childPurchases,
        },
    };
}

export function rollupPlannerQuotes(groups, rates = []) {
    return (groups || []).reduce(
        (acc, g) => {
            if (g.enabled === false) return acc;
            const own = groupQuoteOwn(g, rates);
            acc.laborHours += own.laborHours;
            acc.cost += own.cost;
            acc.purchases += own.purchases;
            acc.total += own.total;
            return acc;
        },
        { laborHours: 0, cost: 0, purchases: 0, total: 0 }
    );
}

export function formatHours(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return "";
    const label = n === 1 ? "hr" : "hrs";
    return `${n.toLocaleString(undefined, { maximumFractionDigits: n % 1 ? 1 : 0 })} ${label}`;
}
